/**
 * Header transport pill (DEC-322, operator 2026-10-27: "top bar needs a
 * transport pill 'playing' or 'paused' or 'stopped'", then "playing should be
 * top right"). Says plainly whether Live is running, which the clock pill (WHICH
 * clock) and the counter (WHERE in the song) do not.
 *
 * Read from the same fresh `/clock/status` snapshot the header already uses
 * (clockPoll.ts keeps reporting the position while Live is stopped):
 *   - playing  : PLAYING
 *   - paused   : not playing, position past the start (Live's first Stop)
 *   - stopped  : not playing, at 1.1.1 (Live's second Stop)
 *   - none     : no fresh report (no OPBridge, or no Live clock): "No transport",
 *                never a guess. The practice clock is not Live's transport.
 *
 * Amber for PAUSED is DEC-321's rule 2 (held, waiting). Non-interactive, like
 * the clock pill. A fixed width so the bar does not shift as the word changes.
 */

import { type Accessor } from 'solid-js';

export type TransportState = 'playing' | 'paused' | 'stopped' | 'none';

const LABEL: Record<TransportState, string> = {
  playing: 'Playing',
  paused: 'Paused',
  stopped: 'Stopped',
  none: 'No transport',
};

/** Pure: the pill's state from a snapshot and whether it is fresh. A position
 *  under a thousandth of a beat counts as the start (Live reports 0 exactly). */
export function transportState(
  snap: { isPlaying: boolean; songTimeBeats: number } | null,
  fresh: boolean,
): TransportState {
  if (!snap || !fresh) return 'none';
  if (snap.isPlaying) return 'playing';
  return snap.songTimeBeats > 0.001 ? 'paused' : 'stopped';
}

interface TransportPillProps {
  state: Accessor<TransportState>;
}

export function TransportPill(props: TransportPillProps) {
  return (
    <div
      class="transport-pill"
      data-state={props.state()}
      title={props.state() === 'none'
        ? 'Live’s transport - no report from OPBridge'
        : `Live’s transport - ${LABEL[props.state()].toLowerCase()}`}
      aria-label={`Live transport: ${LABEL[props.state()]}`}
      role="status"
    >
      <span class="transport-pill-mark" aria-hidden="true" />
      <span class="transport-pill-msg">{LABEL[props.state()]}</span>
    </div>
  );
}
